
import { useState } from 'react'
import { Button, Input, CardHeader } from '../ui'
import { askGemini } from '../../lib/gemini'
import { Mic, Sparkles, Copy, RefreshCw, User, MessageSquare } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export const OpeningSpeechGenerator = ({ tournamentName }) => {
    const { t, i18n } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [speech, setSpeech] = useState('')
    const [formData, setFormData] = useState({
        speakerName: '',
        role: 'Voorzitter',
        tone: 'enthusiastic', // formal, enthusiastic, funny, emotional
        duration: '2', // minutes
        specialPoints: ''
    })

    const generateSpeech = async () => {
        setLoading(true)
        try {
            const toneDesc = {
                formal: t('presentation_page.tone_formal'),
                enthusiastic: t('presentation_page.tone_enthusiastic'),
                funny: t('presentation_page.tone_funny'),
                emotional: t('presentation_page.tone_emotional')
            }[formData.tone]

            const prompt = t('presentation_page.speech_prompt', {
                tournament: tournamentName || t('presentation_page.tv_welcome'),
                speaker: formData.speakerName,
                role: formData.role,
                tone: toneDesc,
                duration: formData.duration,
                points: formData.specialPoints,
                language: i18n.language
            })

            const text = await askGemini(prompt)
            setSpeech(text)
        } catch (err) {
            setSpeech("Error: " + err.message)
        } finally {
            setLoading(false)
        }
    }

    const copyToClipboard = () => {
        navigator.clipboard.writeText(speech)
        alert(t('presentation_page.alert_copied'))
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-slideUp">
            {/* Input Section */}
            <div className="glass-strong rounded-2xl p-6 border border-white/40 shadow-xl h-fit">
                <CardHeader
                    title={t('presentation_page.speech_settings_title')}
                    subtitle={t('presentation_page.speech_settings_subtitle')}
                    icon={Mic}
                    iconColor="from-red-400 to-pink-600"
                />

                <div className="space-y-6 mt-6">
                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            label={t('presentation_page.label_speaker_name')}
                            placeholder={t('presentation_page.placeholder_speaker_name')}
                            value={formData.speakerName}
                            onChange={e => setFormData({ ...formData, speakerName: e.target.value })}
                            icon={<User size={16} />}
                        />
                        <Input
                            label={t('presentation_page.label_role')}
                            placeholder={t('presentation_page.placeholder_role')}
                            value={formData.role}
                            onChange={e => setFormData({ ...formData, role: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">{t('presentation_page.label_tone')}</label>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { id: 'formal', label: t('presentation_page.tone_formal') },
                                { id: 'enthusiastic', label: t('presentation_page.tone_enthusiastic') },
                                { id: 'funny', label: t('presentation_page.tone_funny') },
                                { id: 'emotional', label: t('presentation_page.tone_emotional') }
                            ].map(tone => (
                                <button
                                    key={tone.id}
                                    onClick={() => setFormData({ ...formData, tone: tone.id })}
                                    className={`p-3 rounded-xl border-2 transition-all text-sm font-medium ${formData.tone === tone.id
                                        ? 'border-red-500 bg-red-50 text-red-700 shadow-md'
                                        : 'border-white/30 hover:border-gray-300 bg-white/50 text-gray-600'
                                        }`}
                                >
                                    {tone.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">{t('presentation_page.label_extra_points')}</label>
                        <textarea
                            className="w-full border border-gray-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-red-500 outline-none bg-white/50 min-h-[100px]"
                            placeholder={t('presentation_page.placeholder_extra_points')}
                            value={formData.specialPoints}
                            onChange={e => setFormData({ ...formData, specialPoints: e.target.value })}
                        />
                    </div>

                    <Button
                        onClick={generateSpeech}
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-lg py-4 text-xl font-bold rounded-xl hover:scale-[1.02] transition-transform"
                    >
                        {loading ? <RefreshCw className="animate-spin mr-2" /> : <Sparkles className="mr-2" />}
                        {loading ? t('presentation_page.btn_generating') : t('presentation_page.btn_generate')}
                    </Button>
                </div>
            </div>

            {/* Output Section */}
            <div className="glass-strong rounded-2xl p-8 border border-white/40 shadow-xl min-h-[500px] flex flex-col relative">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <MessageSquare className="text-pink-500" />
                        {t('presentation_page.speech_output_title')}
                    </h3>
                    {speech && (
                        <Button variant="ghost" onClick={copyToClipboard} className="text-gray-600 hover:bg-gray-100">
                            <Copy size={18} className="mr-2" /> {t('presentation_page.btn_copy')}
                        </Button>
                    )}
                </div>

                <div className={`flex-1 rounded-xl p-8 overflow-y-auto leading-relaxed whitespace-pre-line border transition-all ${speech ? 'bg-white border-gray-200 shadow-inner text-gray-800 text-lg' : 'bg-gray-50 border-gray-100 text-gray-400 flex items-center justify-center'}`}>
                    {speech ? (
                        speech
                    ) : (
                        <div className="text-center">
                            <Mic size={48} className="mx-auto mb-4 opacity-20" />
                            <div dangerouslySetInnerHTML={{ __html: t('presentation_page.empty_speech') }} />
                        </div>
                    )}
                </div>

                {speech && (
                    <div className="mt-4 text-center text-xs text-gray-400">
                        {t('presentation_page.speech_tip')}
                    </div>
                )}
            </div>
        </div>
    )
}
